import { useCallback, useEffect, useState } from "react";
import { Switch } from "./Switch";
import { OMRAADER, type Omraade, type Valgt } from "../state/omraade";
import {
  glemRolle, rolleErOverstyrt, settRolle, type Oekt, type Rolle,
} from "../state/session";
import { loggUt } from "../state/innlogging";
import { krevInnlogging } from "../state/supabase";
import type { Sidevalg } from "../state/sidevalg";
import { minePunkter } from "../state/oppsett";
import { hentMitt, hentTeam, velgTeam, type Team } from "../state/team";
import { Merke } from "./Merke";

/**
 * Menyen paa nettflata.
 *
 * DEN DYTTER, DEN DEKKER IKKE. Feltappens meny legger seg over flatene fordi
 * en telefonskjerm er for liten til at to flater deler den. Her er det plass,
 * og da skal ingenting havne bak menyen mens den staar framme. Skallet blir
 * smalere; innholdet reflyter.
 *
 * To ting foelger av det, og begge er med vilje:
 *
 * - INGEN SCRIM, og ingen demping av det som ligger ved siden av. Det er
 *   ikke «bak» noe — det er synlig og brukbart mens menyen staar aapen.
 * - INGEN LUKKING VED TRYKK UTENFOR. Det ville gjort hele flata til en
 *   lukkeflate, og da kan man ikke jobbe i den mens menyen staar der.
 *   Menyen lukkes med ikonet eller Escape.
 *
 * Lukket er den `visibility: hidden` i stilarket, ikke bare null bred. En
 * flate som er klippet til null er fortsatt i tabrekka, og da lander
 * tastaturet i en meny ingen ser.
 *
 * ROLLEN GIR RADER, IKKE EN ANNEN MENY. Naar oppsett kommer, ser prodleder
 * flere rader i den samme menyen — ikke en egen flate. Regelen staar i
 * `OpplastingScreen.tsx`: «Rollen gir ett element mer, ikke en annen flate.»
 * Og over den: rolle styrer skriving og oppsett, ALDRI lesing. Se CLAUDE.md.
 */
/** Rollen slik den leses i menyen. Basen er fasiten; dette er ordene. */
const NAVN: Partial<Record<Rolle, string>> = {
  regi: "regi",
  vj: "VJ",
  redaktor: "redaktør",
  produsent: "produsent",
  klipper: "klipper",
  prodleder: "prodleder",
  crew: "crew",
  reporter: "reporter",
  tekniker: "tekniker",
  afoto: "A-foto",
};

export function Meny({
  aapen, oekt, blackout, omraade, onBlackout, onOmraade, sider, sidevalg,
  flate, onFlate, onLukk, onKonsoll,
}: {
  readonly aapen: boolean;
  readonly oekt: Oekt;
  readonly blackout: boolean;
  readonly omraade: Valgt;
  onBlackout(v: boolean): void;
  onOmraade(o: Omraade, paa: boolean): void;
  readonly sider: readonly { readonly id: string; readonly navn: string }[];
  readonly sidevalg: Sidevalg;
  readonly flate: "arbeid" | "oppsett" | "profil" | "avslutt";
  onFlate(f: "arbeid" | "oppsett" | "profil" | "avslutt"): void;
  onLukk(): void;
  /** Tilbake til konsollen, for aa bytte produksjon eller lage en ny. */
  onKonsoll(): void;
}): JSX.Element {
  /*
   * MENYEN HAR UNDERSIDER NAA.
   *
   * «Sider» er en liste over hver eneste flate, og den hoerer ikke hjemme
   * paa rota: menyen er 320 px bred, og tretten brytere der ville dyttet
   * alt annet ut av syne. Samme loesning som appens meny, og samme grunn.
   */
  const [side, setSide] = useState<"rot" | "sider">("rot");

  /* Har du noe aa sette opp i det hele tatt? Speiler `minePunkter`. */
  const harOppsett = minePunkter(oekt.member.role, oekt.member.extraRoles ?? []).length > 0;

  /* Mister du oppsettsretten mens du staar der — du byttet fagfunksjon —
     skal du ikke bli staaende paa en side du ikke lenger har. */
  useEffect(() => {
    if (!harOppsett && flate === "oppsett") onFlate("arbeid");
  }, [harOppsett, flate, onFlate]);

  /* Lukkes menyen, staar den paa rota neste gang. En meny som husker at du
     var inne i en underside ville aapnet seg et sted du ikke ba om. */
  useEffect(() => { if (!aapen) setSide("rot"); }, [aapen]);

  useEffect(() => {
    if (!aapen) return;
    const paaTast = (e: KeyboardEvent): void => {
      /* Escape gaar ett hakk tilbake foer den lukker. Det er det
         tastaturet gjoer alle andre steder. */
      if (e.key !== "Escape") return;
      if (side !== "rot") { setSide("rot"); return; }
      onLukk();
    };
    window.addEventListener("keydown", paaTast);
    return () => window.removeEventListener("keydown", paaTast);
  }, [aapen, side, onLukk]);

  return (
    <aside
      className={`meny${aapen ? " meny--aapen" : ""}`}
      aria-label="Meny"
      aria-hidden={!aapen}
    >
      {/* Egen innpakning: `.meny` klipper, denne holder bredden mens den
          klippes. Uten den ville innholdet reflytt under animasjonen. */}
      <div className="meny__innhold">
        {/*
          * Merket bor her, ikke i toppstripa.
          *
          * Konsekvensen er at det bare vises naar menyen staar framme. Det
          * er greit: identitet er ikke noe man trenger aa se mens man
          * jobber, og toppstripa har daarlig plass naar menyen har tatt
          * 320 px fra flata.
          */}
        <Merke sti={oekt.production.logoSti} />

        {side === "sider" ? (
          <SiderValg
            sider={sider}
            sidevalg={sidevalg}
            onTilbake={() => setSide("rot")}
          />
        ) : (
        <>

        {/*
          * OMRAADET RYDDER, DET SPERRER IKKE.
          *
          * Den som rigger skal ikke bla forbi dagsplanen for aa komme til
          * kameraplanen. Men «Alt» staar foerst og er alltid ett trykk unna:
          * ingenting er skjult for noen, og det foelger av den ufravikelige
          * regelen om at rolle styrer skriving og oppsett, aldri lesing.
          */}
        {/*
          * TEAMET BLIR I MENYEN, fagfunksjonen flyttet til profilen.
          *
          * Brukeren 27.10.2026: teamet «kan endre seg fra dag til dag».
          * Turnus og sykdom flytter folk, saa det skal vaere ett trykk
          * unna mens man jobber. Fagfunksjonen setter man én gang, og den
          * sier hva du ER — den hoerer paa profilen.
          */}
        <Teamvalg oekt={oekt} />

        {/*
          * TO SIDER PAA OEVERSTE NIVAA, og dette er valget mellom dem.
          *
          * Arbeidsflata er én side med sine faner; oppsettet er den andre
          * med sine tre. Den staar oeverst fordi den avgjoer hva du i det
          * hele tatt ser — alt under gjelder arbeidsflata.
          */}
        <div className="meny__gruppe">
          <span className="meny__gruppenavn">SIDE</span>
          {/*
            * OPPSETT STAAR IKKE DER FOR DEN SOM IKKE HAR NOE DER.
            *
            * Brukeren 27.10.2026: «Hvis man bare er crew, saa trenger ikke
            * Oppsett aa komme opp i menyen i det hele tatt.» Det gjelder
            * alle uten oppsettsrett — regi, VJ, klipper og reporter staar i
            * samme baat. En side som bare sier «ingenting for deg» er en
            * blindvei man maa gaa ned for aa oppdage.
            *
            * Dette er RYDDING, ikke tilgang. Basen nekter uansett, og alle
            * leser alt i produksjonen som foer.
            */}
          {([
            { id: "arbeid", navn: "Arbeidsflate" },
            ...(harOppsett ? [{ id: "oppsett", navn: "Oppsett" } as const] : []),
            /* Profilen har ALLE. Rettighetene etter kapittel III i
               forordningen henger ikke paa en rolle. */
            { id: "profil", navn: "Profil" },
            /*
              * AVSLUTT DAGEN HOERER TIL HER, ikke under loggen.
              *
              * Brukeren 03.11.2026: «Jeg oensker heller at avslutt dagen
              * skal ligge i menyen, slik den gjoer i appen.» Den brukes én
              * gang per dag, og en knapp som avslutter dagen skal ikke
              * ligge i veien paa flata man skriver i hele dagen.
              */
            { id: "avslutt", navn: "Avslutt dagen" },
          ] as const).map((v) => (
            <button
              key={v.id}
              type="button"
              className="meny__rad meny__rad--knapp"
              aria-current={flate === v.id}
              onClick={() => { onFlate(v.id); onLukk(); }}
            >
              <span>{v.navn}</span>
              <span className="meny__radverdi">{flate === v.id ? "✓" : ""}</span>
            </button>
          ))}
        </div>

        {/* Hvilke faner arbeidsflata har. Gjelder ikke oppsettet — det er
            tre felt som alltid staar der. */}
        {flate !== "arbeid" ? null : (
          <div className="meny__gruppe">
            <button
              type="button"
              className="meny__rad meny__rad--knapp"
              onClick={() => setSide("sider")}
            >
              <span>Faner</span>
              <span className="meny__radverdi">
                {sidevalg.paa.size} av {sider.length} ›
              </span>
            </button>
          </div>
        )}

        <div className="meny__gruppe">
          <span className="meny__gruppenavn">OMRÅDE</span>
          {OMRAADER.map((o) => (
            <div key={o.id} className="meny__rad">
              <span>{o.navn}</span>
              <Switch
                value={omraade.paa.has(o.id)}
                label={o.navn}
                onChange={() => onOmraade(o.id, !omraade.paa.has(o.id))}
              />
            </div>
          ))}
        </div>

        <div className="meny__gruppe">
          <span className="meny__gruppenavn">INNSTILLINGER</span>
          <div className="meny__rad">
            <span>Blackout</span>
            <Switch
              value={blackout}
              label="Blackout"
              onChange={() => onBlackout(!blackout)}
            />
          </div>
        </div>

        {/*
          * HVA DU SER PAA OG HVEM DU ER, nederst.
          *
          * Begge er oppslag man gjoer sjelden og aldri handler paa herfra:
          * produksjonens felt settes i Oppsett, og navnet staar alt i
          * toppstripa. De hoerer derfor der oeyet slutter aa lete, ikke
          * mellom valgene man faktisk bruker.
          */}
        <div className="meny__gruppe">
          <span className="meny__gruppenavn">PRODUKSJON</span>
          <span className="meny__lesefelt">
            {oekt.production.name}
            <br />
            Opptaksdag {oekt.shootDay} · {oekt.production.framerate} fps
            {oekt.production.dropFrame ? " drop-frame" : ""}
            <br />
            Slutter {oekt.production.endDateIso}
            <br />
            {oekt.production.nle === null
              ? "Leverer bare PDF"
              : `Klipper i ${oekt.production.nle}`}
          </span>
        </div>

        <div className="meny__gruppe">
          <span className="meny__gruppenavn">KONTO</span>
          <span className="meny__lesefelt">
            {oekt.member.displayName} · {oekt.member.initials}
            <br />
            {NAVN[oekt.member.role] ?? oekt.member.role}
          </span>
          {/*
            * ROLLEBYTTEREN ER BORTE, 27.10.2026, paa bestilling.
            *
            * Den var stillas: uten innlogging fantes det ingen annen maate
            * aa se flata som en annen rolle. Naa settes rollen av
            * fagfunksjonen oeverst i menyen, og den er ekte — utloeseren i
            * basen utleder den, og klienten skriver den ikke.
            */}
          {!krevInnlogging ? null : (
            <>
              {/* Over «Logg ut», fordi det er den man vil ha ni av ti
                  ganger: en frilanser bytter produksjon, hen gaar ikke ut
                  av systemet. */}
              <button
                type="button"
                className="meny__rad meny__rad--knapp"
                onClick={() => { onLukk(); onKonsoll(); }}
              >
                <span>Bytt produksjon</span>
              </button>
              <button
                type="button"
                className="meny__rad meny__rad--knapp"
                onClick={() => { void loggUt(); }}
              >
                <span>Logg ut</span>
              </button>
              {/* Hvorfor dette staar her: oekten ligger i nettleseren, og en
                  maskin i et kontrollrom deles. Da skal det vaere tydelig at
                  man blir vaerende inne til man gaar ut selv. */}
              <span className="meny__merknad">
                Du blir værende innlogget i denne nettleseren. Logger du ut, er
                en ny lenke på e-post veien inn igjen.
              </span>
            </>
          )}
        </div>

        {/*
          * ========  STILLAS  ========
          *
          * Finnes BARE naar innlogging er av. Med auth paa sier
          * `production_member` hva du er, og basen haandhever det — da
          * ville denne vaert en knapp som lyver. Den rydder seg selv bort
          * den dagen auth slaas paa.
          *
          * Den ligger nederst, under konto, fordi den ikke er noe man
          * bruker mens man jobber — men den maa finnes: flatene er
          * rollestyrte, og uten den kan halvparten av dem ikke proeves.
          */}
        {krevInnlogging ? null : <Stillas oekt={oekt} />}
        </>
        )}
      </div>
    </aside>
  );
}

/**
 * Rollebryteren som bare finnes mens vi bygger.
 *
 * Den skriver til `localStorage`, ikke til basen: dette er en
 * FORHAANDSVISNING av flata, ikke en endring av hvem noen er. Velger man en
 * fagfunksjon oeverst i menyen, er DET den ekte veien — den skriver til
 * `production_member`, og utloeseren i basen utleder rollen.
 *
 * «Tilbakestill» er ikke pynt. Noekkelen overlever at bryteren forsvinner,
 * og 27.10.2026 laa den igjen og overstyrte froeet uten at noe i flata
 * kunne roere den. En overstyring maa alltid ha en vei ut.
 */
function Stillas({ oekt }: { readonly oekt: Oekt }): JSX.Element {
  const overstyrt = rolleErOverstyrt();
  return (
    <div className="meny__gruppe meny__gruppe--stillas">
      <span className="meny__gruppenavn">MENS VI BYGGER</span>
      <select
        className="meny__velger"
        value={oekt.member.role}
        onChange={(e) => settRolle(e.target.value as Rolle)}
        aria-label="Se flata som"
      >
        {(Object.keys(NAVN) as Rolle[]).map((r) => (
          <option key={r} value={r}>Se flata som {NAVN[r]}</option>
        ))}
      </select>
      <span className="meny__merknad">
        Bare en forhåndsvisning — den skriver ingenting til produksjonen.
        Forsvinner når innlogging slås på.
      </span>
      {!overstyrt ? null : (
        <button
          type="button"
          className="meny__rad meny__rad--knapp"
          onClick={glemRolle}
        >
          <span>Tilbakestill</span>
          <span className="meny__radverdi">til {NAVN[FROEROLLE] ?? FROEROLLE}</span>
        </button>
      )}
    </div>
  );
}

/** Rollen froeet har naar ingenting er overstyrt. Se state/session.ts. */
const FROEROLLE: Rolle = "prodleder";

/**
 * Lista over flater.
 *
 * ========  LISTA DI, IKKE EN TILGANG  ========
 *
 * Alt kan slås på og av, og ingenting er skjult for noen — det følger av
 * den ufravikelige regelen om at alle medlemmer leser alt i produksjonen.
 * Det dette løser er at oppsettsflatene er noe man er innom én gang og
 * deretter blar forbi hver dag resten av produksjonen.
 *
 * Oppsettet står for seg i lista, fordi det er den bunken man faktisk vil
 * legge bort samlet.
 */
function SiderValg({
  sider, sidevalg, onTilbake,
}: {
  readonly sider: readonly { readonly id: string; readonly navn: string }[];
  readonly sidevalg: Sidevalg;
  onTilbake(): void;
}): JSX.Element {
  return (
    <>
      <button type="button" className="meny__tilbake" onClick={onTilbake}>
        ‹ Tilbake
      </button>

      <div className="meny__gruppe">
        <span className="meny__gruppenavn">FANER</span>
        {sider.map((s) => (
          <div key={s.id} className="meny__rad">
            <span>{s.navn}</span>
            <Switch
              value={sidevalg.paa.has(s.id)}
              label={s.navn}
              onChange={() => sidevalg.veksle(s.id)}
            />
          </div>
        ))}
      </div>

      {/* Veien ut av en tom flate. Uten den kan man slå av den siste sida og
          sitte igjen uten noe å trykke på. */}
      <div className="meny__gruppe">
        <button
          type="button"
          className="meny__rad meny__rad--knapp"
          onClick={sidevalg.visAlle}
        >
          <span>Vis alle</span>
          <span className="meny__radverdi">{sider.length}</span>
        </button>
      </div>
    </>
  );
}


function Teamvalg({ oekt }: { readonly oekt: Oekt }): JSX.Element | null {
  const [team, setTeam] = useState<readonly Team[]>([]);
  const [mitt, setMitt] = useState<string>("");

  useEffect(() => {
    void (async () => {
      setTeam(await hentTeam(oekt.production.id));
      setMitt(await hentMitt(oekt) ?? "");
    })();
  }, [oekt]);

  /* INGEN TEAM ER EN TILSTAND, IKKE INGENTING.
     Ruta forsvant helt, og da saa det ut som at teamvalget ikke fantes
     paa nettflata i det hele tatt. Det gjoer det — det er produksjonen
     som ikke har rigget noen ennaa. */
  if (team.length === 0) {
    return (
      <div className="meny__gruppe">
        <span className="meny__gruppenavn">TEAM</span>
        <span className="meny__lesefelt">
          Ingen team i produksjonen ennå. De settes opp under Oppsett ·
          Produksjon.
        </span>
      </div>
    );
  }

  return (
    <div className="meny__gruppe">
      <span className="meny__gruppenavn">TEAM</span>
      <select
        className="meny__velger"
        value={mitt}
        onChange={(e) => {
          const id = e.target.value;
          setMitt(id);
          void velgTeam(oekt, id);
        }}
        aria-label="Team"
      >
        <option value="">Ikke på et team</option>
        {team.map((t) => (
          <option key={t.id} value={t.id}>{t.navn}</option>
        ))}
      </select>
    </div>
  );
}
